import { isScrollLocked } from "core/scroll-lock";
import { SlideDrawerElement } from "system/slide-drawer";

/**
 * Medidas de caja SIN redondear a entero.
 *
 * Aquí había `Math.round` y costaba un píxel: el borde inferior real de la
 * cabecera cae en 121,1875 (mide 120 + 1px de borde sobre una pantalla a 2x) y
 * se escribía 121, así que el panel arrancaba 0,19px por encima de la barra y
 * por esa rendija asomaba el velo —una línea gris bajo las pestañas que sale o
 * no según el zoom y la densidad de la pantalla, que es la definición de «a
 * veces se corta raro»—. Dos decimales bastan: por debajo de eso ningún
 * dispositivo tiene subpíxeles que enseñar.
 *
 * @param {number} value
 * @returns {number}
 */
function px(value) {
  return Math.round(value * 100) / 100;
}

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = document.querySelector(".section-header");
    this.alwaysSticky = this.dataset.stickyType === "always";
    this.bounds = {};
    this.scrollTop = 0;
    this.preventReveal = false;

    this.setHeight();

    if (this.classList.contains("header-wrapper--transparent")) {
      document.body.classList.add("header-is-transparent");
    }

    this._mq = window.matchMedia("(max-width: 990px)");
    this._onResize = () => this.setHeight();
    this._mq.addEventListener("change", this._onResize);

    if (this.alwaysSticky && this.header) {
      this.header.classList.add("shopify-section-header-sticky");
    }

    // rAF-gate: coalesce a burst of scroll events into one class-write per frame.
    this._scrollRaf = 0;
    this._onScroll = () => {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = 0;
        this.handleScroll();
      });
    };
    window.addEventListener("scroll", this._onScroll, { passive: true });

    // handleScroll() already no-ops while scroll-locked, but that only freezes
    // whatever hidden/visible state the header was already in — if the user
    // had scrolled down (header hidden) right before opening an overlay that
    // locks scroll (search, drawer, cart), the header stayed hidden the whole
    // time, leaving a visible gap above panels anchored to --header-offset
    // (e.g. the search panel). Force a reveal the instant the lock engages.
    this._onScrollLock = () => {
      this.classList.add("scroll-lock-active");
      this.header?.classList.remove("shopify-section-header-hidden");
    };
    this._onScrollUnlock = () => this.classList.remove("scroll-lock-active");
    document.addEventListener("theme:scroll-lock", this._onScrollLock);
    document.addEventListener("theme:scroll-unlock", this._onScrollUnlock);

    if (this.header) {
      const observer = new IntersectionObserver((entries) => {
        this.bounds = entries[0].intersectionRect;
        observer.disconnect();
      });
      observer.observe(this.header);
    }
  }

  disconnectedCallback() {
    document.body.classList.remove("header-is-transparent");
    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    window.removeEventListener("scroll", this._onScroll);
    this._mq.removeEventListener("change", this._onResize);
    document.removeEventListener("theme:scroll-lock", this._onScrollLock);
    document.removeEventListener("theme:scroll-unlock", this._onScrollUnlock);
  }

  setHeight() {
    const topBar = this.querySelector(".header__top-bar");
    const mainBar = this.querySelector(".header");

    // Altura de CAJA, no `offsetHeight`, que devuelve un entero. La barra superior
    // mide 60,1875 (su contenido más el borde de 1px de la fila de abajo, a 2x) y
    // redondeada a 60 dejaba la navegación pegada en y=0,19 en vez de en 0: una
    // rendija de página por encima de la cabecera durante todo el scroll. Es el
    // mismo píxel que se perseguía en `#measure`, y por el mismo motivo.
    const box = (el) => (el ? px(el.getBoundingClientRect().height) : 0);
    const topBarHeight = box(topBar);
    const barHeight = box(mainBar);
    const total = box(this.header) || topBarHeight + barHeight;

    this.topBarHeight = topBarHeight;
    this.barHeight = barHeight;
    this.totalHeight = total;

    document.documentElement.style.setProperty("--header-top-bar-height", `${topBarHeight}px`);
    document.documentElement.style.setProperty("--header-bar-height", `${barHeight}px`);
    document.documentElement.style.setProperty("--header-height", `${total}px`);
    document.documentElement.style.setProperty("--header-offset", `${total}px`);

    this.applyTopBarCollapse();
  }

  /**
   * En móvil la barra superior NO se pega: el sticky de `.section-header` sube su
   * altura (ver header.css) y solo la fila de navegación queda clavada. Aquí se
   * apunta ESE estado, que el CSS por sí solo no puede contar:
   *
   * - `--header-offset` es «cuánta cabecera hay pegada arriba», y de ella cuelgan
   *   el panel de búsqueda, la barra de compra de la PDP y el `scroll-padding` de
   *   los anclajes. Con la barra superior fuera, el número correcto es el de la
   *   fila de navegación sola; dejarlo en el total abría un hueco de página entre
   *   la cabecera y el panel de búsqueda. `--header-height` NO cambia: es la altura
   *   en el FLUJO y de ella depende el hueco que se repone al congelar el body.
   * - La clase `top-bar-collapsed` es para el caso congelado: ahí la cabecera pasa
   *   a `fixed` y pierde el sticky que la colocaba, así que el CSS necesita que
   *   alguien le diga si la barra superior ya se había ido.
   */
  applyTopBarCollapse() {
    if (!this.header) return;

    // El umbral es la altura de la barra superior porque es justo lo que el sticky
    // deja pasar antes de clavarse: por debajo de él la cabecera aún va en el flujo.
    const collapsible = this.topBarHeight > 0 && window.matchMedia("(max-width: 989px)").matches;
    const collapsed = collapsible && window.scrollY > this.topBarHeight;

    this.header.classList.toggle("top-bar-collapsed", collapsed);
    document.documentElement.style.setProperty(
      "--header-offset",
      `${collapsed ? this.barHeight : this.totalHeight}px`,
    );
  }

  handleScroll() {
    if (!this.header) return;
    if (
      isScrollLocked() ||
      this.classList.contains("scroll-lock-active") ||
      this.classList.contains("header-menu-open")
    ) {
      return;
    }

    const y = window.scrollY;

    if (this.classList.contains("header-wrapper--transparent")) {
      this.classList.toggle("is-scrolled", y > 20);
    }

    this.applyTopBarCollapse();

    if (y > this.scrollTop && y > this.bounds.bottom) {
      this.header.classList.add("scrolled-past-header");
      if (this.alwaysSticky) return;
      this.header.classList.add("shopify-section-header-hidden", "shopify-section-header-sticky");
    } else if (y < this.scrollTop && y > this.bounds.bottom) {
      this.header.classList.add("scrolled-past-header");
      if (this.alwaysSticky) return;
      if (!this.preventReveal) {
        this.header.classList.add("shopify-section-header-sticky");
        this.header.classList.remove("shopify-section-header-hidden");
      }
    } else if (y <= this.bounds.top) {
      this.header.classList.remove("scrolled-past-header", "shopify-section-header-hidden", "shopify-section-header-sticky");
    }

    this.scrollTop = y;
  }
}

/**
 * Panel lateral de navegación.
 *
 * El menú de nivel 1 NO está aquí dentro: son las pestañas de la cabecera, que
 * se pintan por encima de este panel (ver la nota de capas en header.nav.css).
 * Este elemento solo decide QUÉ panel se ve y dónde empieza.
 *
 * Tampoco se porta a `<body>` como el carrito: salir del `sticky-header`
 * rompería el contexto de apilamiento común con las pestañas, que es justo lo
 * que permite que no haya dos copias del menú.
 */
class HeaderDrawer extends SlideDrawerElement {
  static slideDirection = "left";

  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** Capas extra (imagen + destacados), una por pestaña que tenga bloque. @type {HTMLElement[]} */
  #features = [];

  /** @type {string} */
  #activeKey = "";

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** Quién abrió: ahí vuelve el foco al cerrar. @type {HTMLElement | null} */
  #opener = null;

  /** @type {boolean} */
  #warmed = false;

  connectedCallback() {
    this.#panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll("[data-nav-panel]")]);
    this.#features = /** @type {HTMLElement[]} */ ([...this.querySelectorAll("[data-nav-feature]")]);

    // Solo son PESTAÑAS las que tienen panel; el resto se quedan como el enlace a
    // su categoría que ya venían siendo (una pestaña sin bloques no despliega).
    //
    // La criba se hace aquí y no en Liquid porque no hay dónde: el contenido del
    // panel son bloques de la sección «Menú lateral» y la barra la pinta la
    // cabecera, que no puede leer los bloques de otra sección. Y de paso sale
    // mejor: sin este módulo no hay panel que abrir, así que tampoco debe haber
    // `aria-controls` prometiendo uno.
    const claves = new Set(this.#panels.map((panel) => panel.dataset.navPanel));
    this.#tabs = /** @type {HTMLElement[]} */ (
      [...document.querySelectorAll("[data-nav-tab]")].filter((tab) => claves.has(tab.dataset.navTab))
    );
    for (const tab of this.#tabs) {
      tab.setAttribute("aria-controls", `HeaderNavPanel-${tab.dataset.navTab}`);
      tab.setAttribute("aria-expanded", "false");
      tab.parentElement?.classList.add("header__menu-item--has-panel");
    }

    this.resolveTrigger();
    super.connectedCallback();
  }

  /** @param {AbortSignal} signal */
  onConnected(signal) {
    for (const tab of this.#tabs) {
      tab.addEventListener(
        "click",
        (event) => {
          // Sin JS la pestaña es un enlace a su categoría (y así se queda si
          // este módulo no llega a cargar). Con JS, abre su panel.
          event.preventDefault();
          this.#opener = tab;
          this.selectPanel(tab.dataset.navTab ?? "");
        },
        { signal },
      );
    }

    // El aspa de la barra de pestañas. Se resuelve a nivel de documento, como las
    // pestañas y por lo mismo: vive FUERA del `<header-drawer>`, así que el enganche
    // que `SlideDrawerElement` hace de `[data-drawer-close]` —que busca dentro de sí
    // mismo— no la alcanza.
    for (const cierre of document.querySelectorAll("[data-nav-close]")) {
      cierre.addEventListener("click", () => this.close(), { signal });
    }

    // La franja superior del panel se ancla a la caja REAL de la barra, así que
    // un cambio de tamaño con el menú abierto hay que volver a medirlo.
    window.addEventListener(
      "resize",
      () => {
        if (this.isOpen) this.#measure();
      },
      { passive: true, signal },
    );

    // …y un cambio de POSICIÓN también. Abrir un overlay con scroll bloqueado
    // fuerza la cabecera a reaparecer (`theme:scroll-lock` le quita la clase que
    // la sube), y esa reaparición es una transición de 0,3s: la medida del
    // instante de abrir es la de una barra a medio bajar —en el peor caso, con el
    // borde inferior todavía fuera de la pantalla— y el panel se quedaba anclado
    // ahí hasta cerrarlo. Se vuelve a medir cuando la barra deja de moverse.
    document.querySelector(".section-header")?.addEventListener(
      "transitionend",
      (event) => {
        if (event.propertyName !== "transform") return;
        if (this.isOpen) this.#measure();
      },
      { signal },
    );
  }

  /** @param {AbortSignal} signal */
  bindDrawerTriggers(signal) {
    this.trigger?.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        this.#opener = this.trigger;
        if (this.isOpen) {
          this.close();
          return;
        }
        this.#showPanel(this.#activeKey || this.#panels[0]?.dataset.navPanel || "");
        this.open();
      },
      { signal },
    );
  }

  resolveTrigger() {
    const id = this.id;
    this.#trigger = /** @type {HTMLElement | null} */ (
      (id ? document.querySelector(`[aria-controls="${id}"]`) : null) ??
        document.querySelector("[data-drawer-open]")
    );
  }

  get trigger() {
    return this.#trigger;
  }

  /**
   * Clic en una pestaña: la misma que ya está abierta cierra; otra intercambia
   * el contenido sin volver a deslizar el panel.
   * @param {string} key
   */
  selectPanel(key) {
    if (!key) return;

    if (this.isOpen && this.#activeKey === key) {
      this.close();
      return;
    }

    this.#showPanel(key);
    if (!this.isOpen) this.open();
  }

  /** @param {string} key */
  #showPanel(key) {
    if (!key) return;
    this.#activeKey = key;

    for (const panel of this.#panels) {
      panel.toggleAttribute("hidden", panel.dataset.navPanel !== key);
    }
    for (const tab of this.#tabs) {
      tab.setAttribute("aria-expanded", String(tab.dataset.navTab === key));
    }

    // La capa extra es opcional por pestaña: la clase del host es la que decide si
    // sale o se queda escondida detrás de la navegación (la transición es CSS).
    let hasFeature = false;
    for (const feature of this.#features) {
      const active = feature.dataset.navFeature === key;
      feature.toggleAttribute("hidden", !active);
      if (active) hasFeature = true;
    }
    this.classList.toggle("has-feature", hasFeature);

    // Cambiar de pestaña con el panel scrolleado dejaba la categoría nueva
    // empezada por la mitad.
    const body = this.querySelector(".header-drawer__body");
    if (body instanceof HTMLElement) body.scrollTop = 0;
  }

  /**
   * Dónde empieza el panel. Se lee de la caja de la barra en vez de sumar
   * variables de altura porque la cabecera puede estar pegada, replegada o aún
   * en el flujo, y solo el rect sabe cuál de las tres.
   *
   * Se escribe en :root y no en la cabecera porque el panel dejó de ser
   * descendiente suyo: vive en el grupo de overlays, al final del body.
   */
  #measure() {
    const bar = document.querySelector("sticky-header .header");
    if (!(bar instanceof HTMLElement)) return;

    const rect = bar.getBoundingClientRect();
    const root = document.documentElement;
    root.style.setProperty("--nav-bar-top", `${Math.max(0, px(rect.top))}px`);
    root.style.setProperty("--nav-bar-bottom", `${Math.max(0, px(rect.bottom))}px`);

    // La ALTURA de la barra, no solo su borde inferior: es lo que la tira de
    // pestañas de móvil necesita para colocarse, y sale del MISMO rect que el
    // borde del panel. Que las dos piezas salgan de la misma medida en el mismo
    // frame es justo lo que garantiza que no puedan desalinearse.
    root.style.setProperty("--nav-bar-height", `${Math.max(0, px(rect.height))}px`);
  }

  /**
   * Al abrir, se piden TODAS las imágenes del menú, no solo las de la pestaña
   * visible.
   *
   * Todo el árbol viene renderizado del servidor, así que cambiar de pestaña no
   * pide nada… salvo las imágenes: nacen `loading="lazy"` y una imagen dentro de
   * un panel oculto no llega a pedirse nunca, así que la segunda pestaña se
   * dibujaba con los huecos vacíos y las fotos entrando de una en una. Pasarlas a
   * `eager` desbloquea la descarga aunque sigan ocultas, y para cuando el usuario
   * cambia de pestaña ya están.
   *
   * Se hace al abrir y no antes: en una visita que no toca el menú no se descarga
   * nada. Y una sola vez.
   */
  #warmContent() {
    if (this.#warmed) return;
    this.#warmed = true;
    for (const img of this.querySelectorAll('img[loading="lazy"]')) {
      img.setAttribute("loading", "eager");
    }
  }

  onDrawerOpen() {
    // `core/boot` puede abrir el drawer directamente (el clic que llega antes de
    // que este módulo ate su listener). Sin panel elegido saldría en blanco.
    if (!this.#activeKey) this.#showPanel(this.#panels[0]?.dataset.navPanel ?? "");
    this.#warmContent();
    this.#measure();
    document.querySelector("sticky-header")?.classList.add("header-menu-open");
  }

  onDrawerClose() {
    document.querySelector("sticky-header")?.classList.remove("header-menu-open");
    for (const tab of this.#tabs) tab.setAttribute("aria-expanded", "false");

    // La hamburguesa se oculta en escritorio: devolverle el foco ahí lo perdería
    // (un elemento con display:none no lo acepta y se cae al <body>).
    const back = this.#opener?.isConnected && this.#opener.offsetParent !== null ? this.#opener : this.trigger;
    back?.focus({ preventScroll: true });
  }
}

function registerHeaderElements() {
  if (!customElements.get("sticky-header")) {
    customElements.define("sticky-header", StickyHeader);
  }
  if (!customElements.get("header-drawer")) {
    customElements.define("header-drawer", HeaderDrawer);
  }
}

export function initHeader() {
  registerHeaderElements();
}

registerHeaderElements();
